import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openTawkChat } from '../common/TawkTo';
import './ParentSections.css';

const ParentSectionSupport = () => {
  const { t } = useTranslation('parent');
  const [activeTab, setActiveTab] = useState('faq');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      category: 'general',
      question: 'How do I add my child to the platform?',
      answer: 'You can add your child by clicking the "Add Child" button in the Children section. Fill in their name, age, grade, email, and create a password for their account.'
    },
    {
      id: 2,
      category: 'general',
      question: 'How can I track my child\'s progress?',
      answer: 'Go to the Progress section and select your child. You will see their overall progress, completed courses, and detailed performance analysis.'
    },
    {
      id: 3,
      category: 'payments',
      question: 'How do I manage my subscription?',
      answer: 'You can view and manage your subscriptions in the Payments section. From there, you can update payment methods, cancel auto-renewal, or change your plan.'
    },
    {
      id: 4,
      category: 'controls',
      question: 'How do I set time limits for my child?',
      answer: 'Go to Parental Controls, select your child, and enable time limits. You can set a daily limit and bedtime to control when your child can access the platform.'
    },
    {
      id: 5,
      category: 'progress',
      question: 'How do I generate a progress report?',
      answer: 'In the Progress section, select your child and click "Generate Report" to create a detailed report of their learning progress and achievements.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    priority: 'normal'
  });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    alert(t('support.contactSubmitted'));
    setContactForm({ subject: '', message: '', priority: 'normal' });
  };

  return (
    <div className="parent-main-content">
      <div className="parent-content-header">
        <h1 className="parent-content-title">{t('support.title')}</h1>
        <p className="parent-content-subtitle">{t('support.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="parent-tabs">
        <button
          className={`parent-tab ${activeTab === 'faq' ? 'active' : ''}`}
          onClick={() => setActiveTab('faq')}
        >
          <i className="material-icons">help_outline</i>
          {t('support.faq')}
        </button>
        <button
          className={`parent-tab ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          <i className="material-icons">mail</i>
          {t('support.contactUs')}
        </button>
        <button
          className={`parent-tab ${activeTab === 'resources' ? 'active' : ''}`}
          onClick={() => setActiveTab('resources')}
        >
          <i className="material-icons">book</i>
          {t('support.resources')}
        </button>
      </div>

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <div className="parent-section">
          <div className="parent-section-header">
            <h2 className="parent-section-title">{t('support.frequentlyAskedQuestions')}</h2>
          </div>

          <div className="parent-search-box">
            <i className="material-icons">search</i>
            <input
              type="text"
              placeholder={t('support.searchFaqs')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="parent-faq-list">
            {filteredFaqs.length === 0 ? (
              <div className="parent-empty-state">
                <i className="material-icons">search_off</i>
                <p>{t('support.noFaqsFound')}</p>
              </div>
            ) : (
              filteredFaqs.map((faq) => (
                <div key={faq.id} className="parent-faq-item">
                  <button
                    className="parent-faq-question"
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                  >
                    <span>{faq.question}</span>
                    <i className={`material-icons ${expandedFaq === faq.id ? 'expanded' : ''}`}>
                      {expandedFaq === faq.id ? 'expand_less' : 'expand_more'}
                    </i>
                  </button>
                  {expandedFaq === faq.id && (
                    <div className="parent-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <div className="parent-section">
          <div className="parent-section-header">
            <h2 className="parent-section-title">{t('support.contactSupport')}</h2>
          </div>

          <form onSubmit={handleContactSubmit} className="parent-form">
            <div className="parent-form-group">
              <label htmlFor="contactSubject">{t('support.subject')} *</label>
              <input
                type="text"
                id="contactSubject"
                value={contactForm.subject}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                required
                placeholder={t('support.subjectPlaceholder')}
              />
            </div>

            <div className="parent-form-group">
              <label htmlFor="contactPriority">{t('support.priority')}</label>
              <select
                id="contactPriority"
                value={contactForm.priority}
                onChange={(e) => setContactForm({ ...contactForm, priority: e.target.value })}
              >
                <option value="low">{t('support.priorityLow')}</option>
                <option value="normal">{t('support.priorityNormal')}</option>
                <option value="high">{t('support.priorityHigh')}</option>
                <option value="urgent">{t('support.priorityUrgent')}</option>
              </select>
            </div>

            <div className="parent-form-group">
              <label htmlFor="contactMessage">{t('support.message')} *</label>
              <textarea
                id="contactMessage"
                rows="6"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                placeholder={t('support.messagePlaceholder')}
              ></textarea>
            </div>

            <div className="parent-form-actions">
              <button type="submit" className="parent-btn primary">
                <i className="material-icons">send</i>
                {t('support.sendMessage')}
              </button>
            </div>
          </form>

          <div className="parent-contact-info">
            <h3>{t('support.otherWaysToContact')}</h3>
            <div className="parent-contact-methods">
              <div className="parent-contact-method">
                <i className="material-icons">chat</i>
                <div>
                  <strong>{t('support.liveChat', 'Live chat')}</strong>
                  <p>{t('support.liveChatDesc', 'Chat with support in real time')}</p>
                  <button type="button" className="parent-btn secondary small" onClick={openTawkChat}>
                    {t('support.startChat', 'Start chat')}
                  </button>
                </div>
              </div>
              <div className="parent-contact-method">
                <i className="material-icons">email</i>
                <div>
                  <strong>{t('support.email')}</strong>
                  <p>support@nativelike.com</p>
                </div>
              </div>
              <div className="parent-contact-method">
                <i className="material-icons">phone</i>
                <div>
                  <strong>{t('support.phone')}</strong>
                  <p>+1 (555) 123-4567</p>
                </div>
              </div>
              <div className="parent-contact-method">
                <i className="material-icons">schedule</i>
                <div>
                  <strong>{t('support.hours')}</strong>
                  <p>Monday - Friday, 9 AM - 6 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resources Tab */}
      {activeTab === 'resources' && (
        <div className="parent-section">
          <div className="parent-section-header">
            <h2 className="parent-section-title">{t('support.helpfulResources')}</h2>
          </div>

          <div className="parent-resources-grid">
            <div className="parent-resource-card">
              <i className="material-icons">video_library</i>
              <h3>{t('support.videoTutorials')}</h3>
              <p>{t('support.videoTutorialsDesc')}</p>
              <button className="parent-btn secondary">
                {t('support.watchNow')}
              </button>
            </div>

            <div className="parent-resource-card">
              <i className="material-icons">description</i>
              <h3>{t('support.userGuide')}</h3>
              <p>{t('support.userGuideDesc')}</p>
              <button className="parent-btn secondary">
                {t('support.download')}
              </button>
            </div>

            <div className="parent-resource-card">
              <i className="material-icons">forum</i>
              <h3>{t('support.community')}</h3>
              <p>{t('support.communityDesc')}</p>
              <button className="parent-btn secondary">
                {t('support.join')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentSectionSupport;









